import type {
  Appointment, Invoice, MarketQuote, Message, Money, Order, OrderChannel,
  OrderLine, Party, PartyBalance, Payment, Product, PurchaseTask, Quantity,
  Route, Session, Shipment, StockLot, Thread, TimeWindow,
} from '@prody/shared';
import {
  DEMAND_ORDER_STATUSES, ageingBucket, buildSupplierDemand, daysOverdue,
  invoiceOutstanding,
} from '@prody/shared';
import * as seed from './seed';

/* ------------------------------------------------------------------ *
 * The data boundary.
 *
 * Every module talks to this interface and to nothing else. Today it
 * is backed by the in-memory seed so the frontend runs and demos on
 * its own; when the NestJS side lands, `createHttpApi` replaces
 * `createMockApi` in one line in `providers.tsx` and no module
 * changes. That swap is the entire point of putting a boundary here
 * rather than calling fetch from a component.
 *
 * Every method takes `businessId` first. It is not decorative: it is
 * the tenant key, it is in every query key, and it is what makes
 * switching business a cache-safe operation instead of a page reload.
 * ------------------------------------------------------------------ */

export interface ListParams {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface OrderFilters extends ListParams {
  deliveryDate?: string;
  statuses?: string[];
  customerId?: string;
  shortOnly?: boolean;
}

/** What the editor sends. Numbers, denormalised names and audit are the server's job. */
export interface NewOrderLineInput {
  productId: string;
  quantity: Quantity;
  unitPrice: Money;
  priceSource: OrderLine['priceSource'];
  note?: string | null;
}

export interface NewOrderInput {
  customerId: string;
  deliveryDate: string;
  deliveryWindow: TimeWindow | null;
  channel: OrderChannel;
  lines: NewOrderLineInput[];
  customerNote?: string | null;
  internalNote?: string | null;
  /** A draft is being typed; `placed` is a promise to the kitchen. */
  status: 'draft' | 'placed';
}

export interface AssignSupplierInput {
  deliveryDate: string;
  productId: string;
  /** null clears the assignment back to "unassigned". */
  supplier: { id: string; name: string } | null;
}

export interface ProdyApi {
  getSession(): Promise<Session>;

  listOrders(businessId: string, filters?: OrderFilters): Promise<Order[]>;
  getOrder(businessId: string, orderId: string): Promise<Order | null>;
  createOrder(businessId: string, input: NewOrderInput): Promise<Order>;
  updateOrder(businessId: string, orderId: string, patch: Partial<Order>): Promise<Order>;
  /** Stamp a supplier onto every open line of a product for a delivery day. */
  assignSupplier(businessId: string, input: AssignSupplierInput): Promise<Order[]>;
  /** Rebuild the day's purchase tasks from live order demand. */
  generatePurchaseTasks(businessId: string, tradingDay: string): Promise<PurchaseTask[]>;

  listQuotes(businessId: string, tradingDay: string): Promise<MarketQuote[]>;
  updateQuote(businessId: string, quoteId: string, patch: Partial<MarketQuote>): Promise<MarketQuote>;

  listPurchaseTasks(businessId: string, tradingDay: string): Promise<PurchaseTask[]>;
  updatePurchaseTask(businessId: string, taskId: string, patch: Partial<PurchaseTask>): Promise<PurchaseTask>;

  listShipments(businessId: string, date: string): Promise<Shipment[]>;
  updateShipment(businessId: string, shipmentId: string, patch: Partial<Shipment>): Promise<Shipment>;
  listRoutes(businessId: string, date: string): Promise<Route[]>;

  listProducts(businessId: string, params?: ListParams): Promise<Product[]>;
  getProduct(businessId: string, productId: string): Promise<Product | null>;

  listParties(businessId: string, params?: ListParams & { role?: string }): Promise<Party[]>;
  getParty(businessId: string, partyId: string): Promise<Party | null>;

  listStock(businessId: string, params?: ListParams): Promise<StockLot[]>;

  listInvoices(businessId: string, params?: ListParams & { direction?: string; status?: string[] }): Promise<Invoice[]>;
  listPayments(businessId: string, params?: ListParams): Promise<Payment[]>;
  listBalances(businessId: string, direction: 'receivable' | 'payable'): Promise<PartyBalance[]>;

  listThreads(businessId: string, params?: ListParams): Promise<Thread[]>;
  listMessages(businessId: string, threadId: string): Promise<Message[]>;
  sendMessage(businessId: string, threadId: string, body: string, channel: string): Promise<Message>;

  listAppointments(businessId: string, from: string, to: string): Promise<Appointment[]>;

  listTeam(businessId: string): Promise<(typeof seed.TEAM)[number][]>;
}

/* ------------------------------------------------------------------ *
 * Mock implementation
 * ------------------------------------------------------------------ */

/** Latency so loading states are exercised in development, not just in prod. */
const LATENCY_MS = import.meta.env.MODE === 'test' ? 0 : 140;

const settle = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(structuredClone(value)), LATENCY_MS));

const matches = (haystack: (string | null | undefined)[], needle: string): boolean => {
  const q = needle.trim().toLowerCase();
  if (!q) return true;
  return haystack.some((h) => h?.toLowerCase().includes(q));
};

export const createMockApi = (): ProdyApi => {
  // Mutable copies so optimistic updates and mutations survive within a session.
  const orders = structuredClone(seed.ORDERS);
  const quotes = structuredClone(seed.QUOTES);
  const tasks = structuredClone(seed.PURCHASE_TASKS);
  const messages = structuredClone(seed.MESSAGES);
  const threads = structuredClone(seed.THREADS);
  const shipments = structuredClone(seed.SHIPMENTS);
  let createdOrderSeq = 0;
  let createdLineSeq = 0;

  const scoped = <T extends { businessId: string }>(rows: T[], businessId: string): T[] =>
    rows.filter((r) => r.businessId === businessId);

  return {
    getSession: () => settle(seed.SESSION),

    listOrders: (businessId, filters = {}) => {
      let rows = scoped(orders, businessId);
      if (filters.deliveryDate) rows = rows.filter((o) => o.deliveryDate === filters.deliveryDate);
      if (filters.statuses?.length) rows = rows.filter((o) => filters.statuses!.includes(o.status));
      if (filters.customerId) rows = rows.filter((o) => o.customerId === filters.customerId);
      if (filters.shortOnly) rows = rows.filter((o) => o.lines.some((l) => l.shortfall));
      if (filters.search) {
        rows = rows.filter((o) =>
          matches([o.number, o.customerName, ...o.lines.map((l) => l.productName)], filters.search!),
        );
      }
      return settle(rows);
    },

    getOrder: (businessId, orderId) =>
      settle(scoped(orders, businessId).find((o) => o.id === orderId) ?? null),

    createOrder: (businessId, input) => {
      const business = seed.BUSINESSES.find((b) => b.id === businessId);
      const customer = seed.PARTIES.find(
        (p) => p.id === input.customerId && p.businessId === businessId,
      );
      if (!business || !customer) return Promise.reject(new Error('Customer not found'));

      // Per-series numbering: YY-NNNN, next free suffix for this year.
      const prefix = String(new Date().getUTCFullYear() % 100).padStart(2, '0');
      const suffix = scoped(orders, businessId)
        .map((o) => /^(\d{2})-(\d{4})$/.exec(o.number))
        .filter((match): match is RegExpExecArray => match?.[1] === prefix)
        .reduce((max, match) => Math.max(max, Number(match[2])), 0);
      const number = `${prefix}-${String(suffix + 1).padStart(4, '0')}`;

      const now = new Date().toISOString();
      const productById = new Map(seed.PRODUCTS.map((p) => [p.id as string, p]));
      const lines: OrderLine[] = input.lines.map((row, i) => {
        const product = productById.get(row.productId);
        return {
          id: `oln_new_${++createdLineSeq}` as OrderLine['id'],
          productId: row.productId as OrderLine['productId'],
          productName: product?.name['en'] ?? product?.sku ?? row.productId,
          sku: product?.sku ?? row.productId,
          ordered: row.quantity,
          picked: null,
          invoiced: null,
          unitPrice: row.unitPrice,
          priceSource: row.priceSource,
          discountBps: 0,
          taxRateKey: product?.taxRateKey ?? business.settings.taxScheme.defaultRateKey,
          supplierId: (product?.defaultSupplierId ?? null) as OrderLine['supplierId'],
          supplierName: product?.defaultSupplierId
            ? seed.PARTIES.find((p) => p.id === product.defaultSupplierId)?.name ?? null
            : null,
          shortfall: null,
          substitution: null,
          note: row.note ?? null,
          sortOrder: i + 1,
        };
      });

      const order: Order = {
        id: `ord_new_${++createdOrderSeq}` as Order['id'],
        businessId: businessId as Order['businessId'],
        custom: {},
        createdAt: now, createdBy: seed.SESSION.user.id, updatedAt: now,
        updatedBy: seed.SESSION.user.id, deletedAt: null,
        number,
        status: input.status,
        customerId: input.customerId as Order['customerId'],
        customerName: customer.name,
        channel: input.channel,
        placedAt: input.status === 'placed' ? now : null,
        deliveryDate: input.deliveryDate,
        deliveryWindow: input.deliveryWindow,
        deliveryAddressId: customer.addresses[0]?.id ?? null,
        currency: business.settings.currency,
        lines,
        documentDiscountBps: 0,
        deliveryFee: null,
        assignedToId: seed.SESSION.user.id,
        shipmentId: null,
        invoiceId: null,
        customerNote: input.customerNote ?? null,
        internalNote: input.internalNote ?? null,
        tags: [],
      };
      orders.push(order);
      return settle(order);
    },

    updateOrder: (businessId, orderId, patch) => {
      const index = orders.findIndex((o) => o.id === orderId && o.businessId === businessId);
      if (index < 0) return Promise.reject(new Error('Order not found'));
      const next = { ...orders[index]!, ...patch, updatedAt: new Date().toISOString() };
      orders[index] = next;
      return settle(next);
    },

    assignSupplier: (businessId, input) => {
      const touched: Order[] = [];
      const now = new Date().toISOString();
      for (const order of orders) {
        if (order.businessId !== businessId) continue;
        if (order.deliveryDate !== input.deliveryDate) continue;
        if (!DEMAND_ORDER_STATUSES.includes(order.status)) continue;
        if (!order.lines.some((l) => l.productId === input.productId)) continue;
        order.lines = order.lines.map((l) =>
          l.productId === input.productId
            ? {
                ...l,
                supplierId: (input.supplier?.id ?? null) as OrderLine['supplierId'],
                supplierName: input.supplier?.name ?? null,
              }
            : l,
        );
        order.updatedAt = now;
        touched.push(order);
      }
      return settle(touched);
    },

    generatePurchaseTasks: (businessId, tradingDay) => {
      const partyById = new Map(seed.PARTIES.map((p) => [p.id as string, p]));
      const productById = new Map(seed.PRODUCTS.map((p) => [p.id as string, p]));
      const demandOrders = scoped(orders, businessId).filter(
        (o) => o.deliveryDate === tradingDay && DEMAND_ORDER_STATUSES.includes(o.status),
      );
      const demand = buildSupplierDemand(demandOrders, (lineRow) => {
        if (lineRow.supplierId) {
          const party = partyById.get(lineRow.supplierId);
          return { id: lineRow.supplierId, name: lineRow.supplierName ?? party?.name ?? '—' };
        }
        const fallback = productById.get(lineRow.productId)?.defaultSupplierId;
        if (!fallback) return null;
        const party = partyById.get(fallback as string);
        return party ? { id: party.id, name: party.name } : null;
      });

      const now = new Date().toISOString();
      const costFor = new Map(
        scoped(quotes, businessId)
          .filter((q) => q.tradingDay === tradingDay)
          .map((q) => [q.productId as string, q.cost]),
      );
      const next: PurchaseTask[] = demand.map((row, i) => {
        const supplierParty = row.supplierId ? partyById.get(row.supplierId) : undefined;
        return {
          id: `pt_gen_${tradingDay}_${i + 1}` as PurchaseTask['id'],
          businessId: businessId as PurchaseTask['businessId'],
          custom: {},
          createdAt: now, createdBy: seed.SESSION.user.id, updatedAt: now,
          updatedBy: seed.SESSION.user.id, deletedAt: null,
          shoppingListId: `sl_${tradingDay}`,
          productId: row.productId as PurchaseTask['productId'],
          productName: row.productName,
          sku: row.sku,
          supplierId: (row.supplierId ?? null) as PurchaseTask['supplierId'],
          supplierName: row.supplierName,
          standCode: supplierParty?.supplier?.standCode ?? null,
          required: row.total,
          buffer: null,
          bought: null,
          expectedCost: costFor.get(row.productId) ?? null,
          actualCost: null,
          status: 'todo',
          demandBreakdown: row.orders.map((o) => ({
            orderId: o.orderId as PurchaseTask['demandBreakdown'][number]['orderId'],
            orderNumber: o.orderNumber,
            customerName: o.customerName,
            quantity: o.quantity,
          })),
          note: null,
          completedAt: null,
          sortOrder: i + 1,
        };
      });

      // Replace this business's list wholesale — regeneration, not append.
      for (let i = tasks.length - 1; i >= 0; i -= 1) {
        if (tasks[i]!.businessId === businessId) tasks.splice(i, 1);
      }
      tasks.push(...next);
      return settle(next);
    },

    listQuotes: (businessId, tradingDay) =>
      settle(scoped(quotes, businessId).filter((q) => q.tradingDay === tradingDay)),

    updateQuote: (businessId, quoteId, patch) => {
      const index = quotes.findIndex((q) => q.id === quoteId && q.businessId === businessId);
      if (index < 0) return Promise.reject(new Error('Quote not found'));
      const next = { ...quotes[index]!, ...patch, updatedAt: new Date().toISOString() };
      quotes[index] = next;
      return settle(next);
    },

    listPurchaseTasks: (businessId) => settle(scoped(tasks, businessId)),

    updatePurchaseTask: (businessId, taskId, patch) => {
      const index = tasks.findIndex((t) => t.id === taskId && t.businessId === businessId);
      if (index < 0) return Promise.reject(new Error('Task not found'));
      const next = { ...tasks[index]!, ...patch, updatedAt: new Date().toISOString() };
      tasks[index] = next;
      return settle(next);
    },

    listShipments: (businessId, date) =>
      settle(scoped(shipments, businessId).filter((s) => s.date === date)),

    updateShipment: (businessId, shipmentId, patch) => {
      const index = shipments.findIndex(
        (s) => s.id === shipmentId && s.businessId === businessId,
      );
      if (index < 0) return Promise.reject(new Error('Shipment not found'));
      const next = { ...shipments[index]!, ...patch, updatedAt: new Date().toISOString() };
      shipments[index] = next;
      return settle(next);
    },

    listRoutes: (businessId, date) =>
      settle(scoped(seed.ROUTES, businessId).filter((r) => r.date === date)),

    listProducts: (businessId, params = {}) => {
      let rows = scoped(seed.PRODUCTS, businessId);
      if (params.search) {
        rows = rows.filter((p) => matches([p.sku, ...Object.values(p.name)], params.search!));
      }
      return settle(rows);
    },

    getProduct: (businessId, productId) =>
      settle(scoped(seed.PRODUCTS, businessId).find((p) => p.id === productId) ?? null),

    listParties: (businessId, params = {}) => {
      let rows = scoped(seed.PARTIES, businessId);
      if (params.role) rows = rows.filter((p) => p.roles.includes(params.role as never));
      if (params.search) {
        rows = rows.filter((p) => matches([p.code, p.name, p.legalName, p.taxId], params.search!));
      }
      return settle(rows);
    },

    getParty: (businessId, partyId) =>
      settle(scoped(seed.PARTIES, businessId).find((p) => p.id === partyId) ?? null),

    listStock: (businessId, params = {}) => {
      let rows = scoped(seed.STOCK_LOTS, businessId);
      if (params.search) rows = rows.filter((l) => matches([l.sku, l.productName, l.lotCode], params.search!));
      return settle(rows);
    },

    listInvoices: (businessId, params = {}) => {
      let rows = scoped(seed.INVOICES, businessId);
      if (params.direction) rows = rows.filter((i) => i.direction === params.direction);
      if (params.status?.length) rows = rows.filter((i) => params.status!.includes(i.status));
      if (params.search) rows = rows.filter((i) => matches([i.number, i.partyName, i.partyTaxId], params.search!));
      return settle(rows);
    },

    listPayments: (businessId, params = {}) => {
      let rows = scoped(seed.PAYMENTS, businessId);
      if (params.search) rows = rows.filter((p) => matches([p.partyName, p.reference], params.search!));
      return settle(rows);
    },

    listBalances: (businessId, direction) => {
      const today = new Date().toISOString().slice(0, 10);
      const wanted = direction === 'receivable' ? 'sales' : 'purchase';
      const byParty = new Map<string, PartyBalance>();

      for (const inv of scoped(seed.INVOICES, businessId)) {
        if (inv.direction !== wanted) continue;
        if (inv.status === 'paid' || inv.status === 'void' || inv.status === 'draft') continue;

        const out = invoiceOutstanding(inv);
        if (out.minor <= 0) continue;
        const late = daysOverdue(inv, today);

        const existing = byParty.get(inv.partyId) ?? {
          partyId: inv.partyId,
          partyName: inv.partyName,
          direction,
          outstanding: { minor: 0, currency: inv.currency },
          overdue: { minor: 0, currency: inv.currency },
          oldestDays: 0,
          creditLimitMinor:
            seed.PARTIES.find((p) => p.id === inv.partyId)?.paymentTerms.creditLimitMinor ?? null,
          invoiceCount: 0,
          buckets: { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90plus: 0 },
        };

        existing.outstanding.minor += out.minor;
        if (late > 0) existing.overdue.minor += out.minor;
        existing.oldestDays = Math.max(existing.oldestDays, late);
        existing.invoiceCount += 1;
        existing.buckets[ageingBucket(late)] += out.minor;
        byParty.set(inv.partyId, existing);
      }

      return settle([...byParty.values()].sort((a, b) => b.outstanding.minor - a.outstanding.minor));
    },

    listThreads: (businessId, params = {}) => {
      let rows = scoped(threads, businessId);
      if (params.search) rows = rows.filter((t) => matches([t.partyName, t.lastMessagePreview], params.search!));
      return settle([...rows].sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt)));
    },

    listMessages: (_businessId, threadId) =>
      settle(messages.filter((msg) => msg.threadId === threadId)),

    sendMessage: (_businessId, threadId, body, channel) => {
      const msg: Message = {
        id: `msg_${messages.length + 1}`,
        threadId: threadId as Message['threadId'],
        channel: channel as Message['channel'],
        direction: 'out',
        status: 'sent',
        authorId: seed.SESSION.user.id,
        authorName: seed.SESSION.user.name,
        body,
        attachments: [],
        transcript: null,
        relatedOrderId: null,
        templateKey: null,
        sentAt: new Date().toISOString(),
        failureReason: null,
      };
      messages.push(msg);
      const thread = threads.find((t) => t.id === threadId);
      if (thread) {
        thread.lastMessageAt = msg.sentAt;
        thread.lastMessagePreview = body;
        thread.lastMessageChannel = msg.channel;
        thread.unreadCount = 0;
      }
      return settle(msg);
    },

    listAppointments: (businessId, from, to) =>
      settle(
        scoped(seed.APPOINTMENTS, businessId).filter(
          (a) => a.startsAt.slice(0, 10) >= from && a.startsAt.slice(0, 10) <= to,
        ),
      ),

    listTeam: (businessId) => settle(seed.TEAM.filter((t) => t.businessId === businessId)),
  };
};
